/* eslint-disable @typescript-eslint/no-use-before-define */
import { createContext, useContext, useState } from 'react'
import type { PropsWithChildren } from 'react'

import { useEditProductMutation } from '@Store/api/product'
import type { Product } from '@Store/types/product'

import {
  BaseModal,
  type BaseModalDispatch,
  BaseModalProvider,
  type BaseModalState,
  useBaseModalDispatch,
  useBaseModalState,
} from './BaseModal'

interface EditProductState extends BaseModalState {
  product?: Product
  isEditProductLoading: boolean
  isEditProductSuccess: boolean
  isEditProductError: boolean
  editProductError?: any
}

interface EditProductDispatch extends BaseModalDispatch {
  setProduct: (payload: Product) => void
  editProduct: (payload?: any) => void
  resetEditProduct: () => void
}

const EditProductStateContext = createContext<EditProductState>(
  {} as EditProductState,
)
EditProductStateContext.displayName = 'EditProductState'

const EditProductDispatchContext = createContext<EditProductDispatch>(
  {} as EditProductDispatch,
)
EditProductDispatchContext.displayName = 'EditProductDispatch'

function EditProductProvider({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  return (
    <BaseModalProvider>
      <EditProductProviderInner modalContent={modalContent}>
        {children}
      </EditProductProviderInner>
    </BaseModalProvider>
  )
}

function EditProductProviderInner({
  children,
  modalContent,
}: PropsWithChildren & { modalContent?: JSX.Element }): JSX.Element {
  const [product, setProduct] = useState<Product>()

  const baseState = useBaseModalState()
  const baseDispatch = useBaseModalDispatch()

  const [
    editProduct,
    {
      isLoading: isEditProductLoading,
      isSuccess: isEditProductSuccess,
      isError: isEditProductError,
      error: editProductError,
      reset: resetEditProduct,
    },
  ] = useEditProductMutation()

  return (
    <EditProductStateContext.Provider
      value={{
        ...baseState,
        product,
        isEditProductLoading,
        isEditProductSuccess,
        isEditProductError,
        editProductError,
      }}
    >
      <EditProductDispatchContext.Provider
        value={{ ...baseDispatch, setProduct, editProduct, resetEditProduct }}
      >
        <BaseModal
          isOpen={baseState.isOpen}
          close={baseDispatch.close}
          onClose={baseDispatch.onClose}
        >
          {modalContent}
        </BaseModal>
        {children}
      </EditProductDispatchContext.Provider>
    </EditProductStateContext.Provider>
  )
}

function useEditProductState(): EditProductState {
  try {
    const context = useContext(EditProductStateContext)
    return context
  } catch (error) {
    throw new Error(
      'useEditProductState must be used within a EditProductProvider',
    )
  }
}

function useEditProductDispatch(): EditProductDispatch {
  try {
    const context = useContext(EditProductDispatchContext)
    return context
  } catch (error) {
    throw new Error(
      'useEditProductDispatch must be used within a EditProductProvider',
    )
  }
}

export { EditProductProvider, useEditProductState, useEditProductDispatch }
